import React, { useState } from 'react';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import Breadcrumb from '../components/Breadcrumb';
import FallGardenClubFilters from '../components/fallGardenClub/FallGardenClubFilters';
import FallGardenClubResourceCard from '../components/fallGardenClub/FallGardenClubResourceCard';
import { useFallGardenClub } from '../hooks/useFallGardenClub';
import { Loader2, Leaf } from 'lucide-react';

export default function FallGardenClub() {
  const { resources, categories, loading, error } = useFallGardenClub();
  const { setItems } = useBreadcrumb();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  React.useEffect(() => {
    setItems([
      { label: 'Home', path: '/' },
      { label: 'Fall Garden Club', path: '/fall-garden-club' },
    ]);
  }, [setItems]);

  const filteredResources = resources.filter((resource) => {
    const matchesCategory =
      !selectedCategory || resource.category?.slug === selectedCategory;
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      resource.title.toLowerCase().includes(searchLower) ||
      (resource.description?.toLowerCase().includes(searchLower) ?? false);
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />

        {/* Hero Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-primary-100 p-3 rounded-full">
              <Leaf className="h-7 w-7 text-primary-600" />
            </div>
            <h1 className="text-3xl font-bold">Fall Garden Club</h1>
          </div>
          <p className="text-gray-600 text-lg max-w-2xl">
            Resources, guides, and tools to help you make the most of your fall garden.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <FallGardenClubFilters
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>

          {/* Resources Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
              </div>
            ) : error ? (
              <div className="text-red-600">{error}</div>
            ) : filteredResources.length === 0 ? (
              <div className="text-center py-12">
                <Leaf className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No resources found</p>
                {(selectedCategory || searchQuery) && (
                  <button
                    onClick={() => {
                      setSelectedCategory(null);
                      setSearchQuery('');
                    }}
                    className="mt-4 text-primary-600 hover:text-primary-700 underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map((resource) => (
                  <FallGardenClubResourceCard key={resource.id} resource={resource} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
